// Cancelling a bill, from the matter's list or the bill's own page. The
// question asked and the readers refreshed have to be the same in both
// places, so the button owns them rather than each screen.

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { voidInvoice } from "../api/caseInvoices";
import { fmtCents } from "../../../lib/money";

interface Props {
  invoiceId: string;
  /** The bill's own matter - its lists are readers of this write. */
  caseId: string;
  number?: string | null;
  amountCents: number;
  onVoided?: () => void;
  onError?: (message: string) => void;
}

export function VoidInvoiceButton({
  invoiceId,
  caseId,
  number,
  amountCents,
  onVoided,
  onError,
}: Props) {
  const queryClient = useQueryClient();

  const voidMut = useMutation({
    mutationFn: () => voidInvoice(invoiceId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["case-invoices", caseId] }),
        // A voided part changes what the plan still owes, and the firm
        // ledger holds its answer for a minute.
        queryClient.invalidateQueries({ queryKey: ["case-plans", caseId] }),
        queryClient.invalidateQueries({ queryKey: ["billing"] }),
      ]);
      onVoided?.();
    },
    // A refused void is a failure, not a status line - same weight as a
    // failed create.
    onError: (err) => onError?.(err instanceof Error ? err.message : "Could not void it."),
  });

  return (
    <button
      type="button"
      className="cc-link-btn danger"
      disabled={voidMut.isPending}
      onClick={() => {
        if (
          window.confirm(
            `Void ${number ?? "this invoice"} for ${fmtCents(amountCents)}? It cannot be un-voided, and it disappears from the client's portal.`,
          )
        ) {
          voidMut.mutate();
        }
      }}
    >
      Void
    </button>
  );
}
